import { navigate } from 'gatsby';
import React, { FC, ReactNode, useContext, useEffect, useState } from 'react';

import useMemberInsuranceInfo from '@hooks/useMemberInsurance';

import { getPathPrefixFromPathname } from '@utils/flowHelpers';

import FlowContext, { FlowContextProvider } from '@providers/FlowContextProvider';
import { IInsuranceProviderSelection, InsuranceContext } from '@providers/InsuranceProvider';

import { FlowIdTypes, FlowTypes } from '@folx-types/flow';

import { insurancePromptConstants } from '../InsurancePromptStep';

interface IInsuranceFlowRouteProps {
	children: ReactNode;
	stepKey: string;
	flowId: FlowIdTypes | FlowTypes;
	path: FlowIdTypes | FlowTypes;
	skipTo?: string;
	backTo?: string;
}

const InsuranceFlowRoute: FC<IInsuranceFlowRouteProps> = ({
	children,
	stepKey: parentStepKey,
	path,
	flowId,
	skipTo,
	backTo,
	...props
}) => {
	const [provider, setProvider] = useState<IInsuranceProviderSelection | undefined>();
	const context = useContext(FlowContext);
	const {
		existingFormData,
		submitFormData,
		handleFormSubmission,
		handleNavBack: baseNavBack,
		handleNavNext: baseNavNext,
		hideNavBack,
		parentRef,
		destinationFlow,
	} = context;

	const pathPrefix = getPathPrefixFromPathname(location.pathname, path);

	useEffect(() => {
		if (!existingFormData?.[insurancePromptConstants.USE_INSURANCE]) {
			navigate(`/${pathPrefix}/${props?.location?.state?.reverse ? backTo : skipTo}`);
		} else {
			navigate(`/${pathPrefix}/${path}/insurance_selection`);
		}
	}, []);

	useEffect(() => {
		if (provider?.name === 'Other') {
			navigate(`/${pathPrefix}/${skipTo}`);
		}
	}, [provider]);

	const {
		memberInsuranceInfo,
		error: errorMemberInsuranceInfo,
		loading: memberInsuranceInfoLoading,
	} = useMemberInsuranceInfo();

	const handleNavNext = () => {
		return baseNavNext(parentStepKey);
	};

	const handleNavBack = () => {
		return baseNavBack(parentStepKey);
	};

	return (
		<FlowContextProvider
			value={{
				destinationFlow: flowId,
				existingFormData,
				flow: path,
				flowId,
				handleFormSubmission,
				handleNavBack,
				handleNavNext,
				submitFormData,
				hideNavBack,
				parentRef,
			}}>
			<InsuranceContext.Provider
				value={{ provider, setProvider, memberInsuranceInfo, memberInsuranceInfoLoading }}>
				{children}
			</InsuranceContext.Provider>
		</FlowContextProvider>
	);
};

export default InsuranceFlowRoute;
